/**
 * CascadingSelectEditor
 * Configuration for cascading-select (dependent dropdowns)
 * Uses COMPOSITE PATTERN for dependency configuration
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface CascadeLevel {
  id: string;
  label: string;
  sourceUrl?: string;
  parentId?: string;
}

interface CascadingSelectConfig {
  levels?: CascadeLevel[];
  dataSource?: Record<string, Array<{ id: string; label: string }>>;
  apiEndpoint?: string;
}

function renderCascadingSelectEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as CascadingSelectConfig;

  const levels = config.levels || [
    { id: 'level-1', label: 'Level 1' },
    { id: 'level-2', label: 'Level 2' },
  ];

  const handleAddLevel = useCallback(() => {
    const newLevels = [...levels, { id: `level-${levels.length + 1}`, label: `Level ${levels.length + 1}` }];
    onConfigChange('levels', newLevels);
  }, [levels, onConfigChange]);

  const handleRemoveLevel = useCallback(
    (index: number) => {
      onConfigChange('levels', levels.filter((_, i) => i !== index));
    },
    [levels, onConfigChange],
  );

  const handleUpdateLevel = useCallback(
    (index: number, field: keyof CascadeLevel, value: string) => {
      const updated = levels.map((l, i) =>
        i === index ? { ...l, [field]: value } : l,
      );
      onConfigChange('levels', updated);
    },
    [levels, onConfigChange],
  );

  const handleDataSourceChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      try {
        const parsed = JSON.parse(e.target.value);
        onConfigChange('dataSource', parsed);
      } catch {
        // Invalid JSON
      }
    },
    [onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Cascading Select Configuration</h4>

      <div className="af-properties-tab__group">
        <label htmlFor="api-endpoint-input" className="af-properties-tab__label">
          API Endpoint (for dynamic loading)
        </label>
        <input
          id="api-endpoint-input"
          type="text"
          value={config.apiEndpoint || ''}
          onChange={(e) => onConfigChange('apiEndpoint', e.target.value)}
          placeholder="https://api.example.com/cascade"
          className="af-properties-tab__input"
          aria-label="API endpoint"
        />
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label" style={{ marginBottom: 6 }}>
          Cascade Levels
        </label>
        {levels.map((level, i) => (
          <div key={i} className="af-properties-tab__cascade-level-row">
            <input
              type="text"
              value={level.label}
              onChange={(e) => handleUpdateLevel(i, 'label', e.target.value)}
              className="af-properties-tab__input af-properties-tab__cascade-label"
              placeholder="Level label"
              aria-label={`Level ${i + 1} label`}
            />
            <input
              type="text"
              value={level.id}
              onChange={(e) => handleUpdateLevel(i, 'id', e.target.value)}
              className="af-properties-tab__input af-properties-tab__cascade-id"
              placeholder="level-id"
              aria-label={`Level ${i + 1} ID`}
            />
            {i > 0 && (
              <select
                value={level.parentId || ''}
                onChange={(e) => handleUpdateLevel(i, 'parentId', e.target.value)}
                className="af-properties-tab__select af-properties-tab__cascade-parent"
              >
                <option value="">Select parent level</option>
                {levels.slice(0, i).map((l, idx) => (
                  <option key={idx} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => handleRemoveLevel(i)}
              className="af-properties-tab__button af-properties-tab__button--danger"
              type="button"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={handleAddLevel}
          className="af-properties-tab__button af-properties-tab__button--primary"
          type="button"
        >
          Add Level
        </button>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="data-source-textarea" className="af-properties-tab__label">
          Static Data Source (JSON)
        </label>
        <textarea
          id="data-source-textarea"
          value={JSON.stringify(config.dataSource || {}, null, 2)}
          onChange={handleDataSourceChange}
          placeholder='{"level-1":[{"id":"1","label":"Option 1"}]}'
          rows={6}
          className="af-properties-tab__textarea"
          aria-label="Data source JSON"
        />
      </div>
    </div>
  );
}

export const CascadingSelectEditor: PropertyEditor = {
  type: 'cascading-select',
  label: 'Cascading Select',
  description: 'Configure dependent select dropdowns with cascade logic',
  render: renderCascadingSelectEditor,
};
